import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus, Edit, Ban, CheckCircle, Eye, Loader2, Shield, ShieldOff, ArrowRightLeft } from 'lucide-react'
import { useUserDetail, useUsers } from '@/hooks/useUsers'
import { CreateUserForm } from './CreateUserForm'
import { EditUserForm } from './EditUserForm'
import { UserDetailDrawer } from './UserDetailDrawer'
import { UserStatusConfirmDialog } from './UserStatusConfirmDialog'
import { SuperAdminConfirmDialog } from './SuperAdminConfirmDialog'
import { TransferUserDialog } from './TransferUserDialog'
import { usersApi } from '@/api/users'
import type { User, UserListItem } from '@/api/users'
import { UserSearchForm, UserSearchParams } from './UserSearchForm'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useUser } from '@/stores/app'
import { showError } from '@/lib/error'

// 状态配置：后端 1=正常, 0=禁用
const statusConfig: Record<number, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
	1: { label: '正常', variant: 'default' },
	0: { label: '禁用', variant: 'destructive' },
}

const genderConfig: Record<number, string> = {
	0: '未知',
	1: '男',
	2: '女',
}

const parseUserSearchParams = (searchParams: URLSearchParams): UserSearchParams => {
	const params: UserSearchParams = {}
	const username = searchParams.get('username')
	const nickname = searchParams.get('nickname')
	const email = searchParams.get('email')
	const phone = searchParams.get('phone')
	const department_id = searchParams.get('department_id')
	const status = searchParams.get('status')
	const is_super_admin = searchParams.get('is_super_admin')
	const start_date = searchParams.get('start_date')
	const end_date = searchParams.get('end_date')

	if (username) params.username = username
	if (nickname) params.nickname = nickname
	if (email) params.email = email
	if (phone) params.phone = phone
	if (department_id) params.department_id = parseInt(department_id, 10)
	if (status) params.status = parseInt(status, 10)
	if (is_super_admin) params.is_super_admin = parseInt(is_super_admin, 10)
	if (start_date) params.start_date = start_date
	if (end_date) params.end_date = end_date

	return params
}

const isSameUserSearchParams = (left: UserSearchParams, right: UserSearchParams) => (
	left.username === right.username &&
	left.nickname === right.nickname &&
	left.email === right.email &&
	left.phone === right.phone &&
	left.department_id === right.department_id &&
	left.status === right.status &&
	left.is_super_admin === right.is_super_admin &&
	left.start_date === right.start_date &&
	left.end_date === right.end_date
)

export function UsersListPage() {
	const queryClient = useQueryClient()
	const { user: currentUser } = useUser()
	const [searchParams, setSearchParams] = useSearchParams()
	const [createDialogOpen, setCreateDialogOpen] = useState(false)
	const [editingUser, setEditingUser] = useState<User | null>(null)
	const [editDialogOpen, setEditDialogOpen] = useState(false)
	const [statusUser, setStatusUser] = useState<UserListItem | null>(null)
	const [statusDialogOpen, setStatusDialogOpen] = useState(false)
	const [revokeUser, setRevokeUser] = useState<UserListItem | null>(null)
	const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)
	const [transferTarget, setTransferTarget] = useState<UserListItem | null>(null)
	const [transferDialogOpen, setTransferDialogOpen] = useState(false)
	const [detailUserId, setDetailUserId] = useState<number | null>(null)
	const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)
	const [loadingEditUserId, setLoadingEditUserId] = useState<number | null>(null)
	const [pageSize, setPageSize] = useState(() => parseInt(searchParams.get('page_size') || String(DEFAULT_PAGE_SIZE), 10))
	const searchParamString = searchParams.toString()

	// 从 URL 初始化搜索参数
	const [searchValues, setSearchValues] = useState<UserSearchParams>(() => parseUserSearchParams(searchParams))

	useEffect(() => {
		const nextSearchValues = parseUserSearchParams(new URLSearchParams(searchParamString))
		setSearchValues((currentSearchValues) => (
			isSameUserSearchParams(currentSearchValues, nextSearchValues) ? currentSearchValues : nextSearchValues
		))
	}, [searchParamString])

	// 从 URL 获取页码
	const page = parseInt(searchParams.get('page') || '1', 10)

	// 查询用户列表
	const { data, isLoading, isError, error, refetch, isFetching } = useUsers({
		page,
		page_size: pageSize,
		...searchValues,
	})
	const { data: detailUser, isLoading: isDetailLoading } = useUserDetail(detailUserId, detailDrawerOpen)

	// 同步搜索参数到 URL
	const syncToUrl = (params: UserSearchParams, newPage = 1, newPageSize = pageSize) => {
		const urlParams = new URLSearchParams()
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== '') {
				urlParams.set(key, String(value))
			}
		})
		if (newPage > 1) {
			urlParams.set('page', String(newPage))
		}
		if (newPageSize !== DEFAULT_PAGE_SIZE) {
			urlParams.set('page_size', String(newPageSize))
		}
		setSearchParams(urlParams, { replace: true })
	}

	// 搜索按钮点击
	const handleSearch = () => {
		syncToUrl(searchValues, 1)
	}

	// 重置按钮点击
	const handleReset = () => {
		setSearchValues({})
		syncToUrl({}, 1)
	}

	// 分页改变
	const handlePageChange = (newPage: number) => {
		syncToUrl(searchValues, newPage)
	}

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize)
		syncToUrl(searchValues, 1, newPageSize)
	}

	const handleOpenDetail = (user: UserListItem) => {
		setDetailUserId(user.id)
		setDetailDrawerOpen(true)
	}

	const handleOpenEdit = async (user: UserListItem) => {
		setLoadingEditUserId(user.id)
		try {
			const fullUser = await queryClient.fetchQuery({
				queryKey: ['users', 'detail', user.id],
				queryFn: () => usersApi.get(user.id),
			})
			setEditingUser(fullUser)
			setEditDialogOpen(true)
		} catch (error) {
			console.error('获取用户详情失败:', error)
			toast.error('获取用户详情失败')
		} finally {
			setLoadingEditUserId(null)
		}
	}

	const superAdminMutation = useMutation({
		mutationFn: ({ id, is_super_admin }: { id: number; is_super_admin: boolean }) =>
			usersApi.updateSuperAdmin(id, is_super_admin),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] })
			toast.success('超管状态更新成功')
		},
		onError: (error) => showError(error, '超管状态更新失败'),
	})

	const handleSetSuperAdmin = (user: UserListItem) => {
		superAdminMutation.mutate({ id: user.id, is_super_admin: true })
	}

	const handleConfirmRevoke = () => {
		if (!revokeUser) return
		superAdminMutation.mutate({ id: revokeUser.id, is_super_admin: false })
		setRevokeDialogOpen(false)
	}

	return (
		<div className="space-y-6">
			{/* 页面标题 */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">用户管理</h1>
					<p className="text-muted-foreground">管理系统中的所有用户账户</p>
				</div>
				<PermissionGuard permission="user:create">
					<Button onClick={() => setCreateDialogOpen(true)}>
						<Plus className="mr-1.5 h-4 w-4" />
						新增用户
					</Button>
				</PermissionGuard>
			</div>

			{/* 搜索筛选表单 */}
			<Card>
				<CardContent className="pt-6">
					<UserSearchForm
						value={searchValues}
						onChange={setSearchValues}
						onSearch={handleSearch}
						onReset={handleReset}
					/>
				</CardContent>
			</Card>

			{/* 用户表格 */}
			<Card>
				<CardContent className="p-0">
					{isLoading ? (
						<div className="p-4 space-y-3">
							{Array.from({ length: 5 }).map((_, i) => (
								<div key={i} className="flex items-center gap-4">
									<Skeleton className="h-10 w-10 rounded-full" />
									<div className="flex-1 space-y-2">
										<Skeleton className="h-4 w-32" />
										<Skeleton className="h-3 w-48" />
									</div>
									<Skeleton className="h-6 w-16" />
								</div>
							))}
						</div>
					) : isError ? (
						<div className="p-8 text-center">
							<p className="text-destructive">加载失败: {(error as Error).message}</p>
							<Button variant="outline" className="mt-4" onClick={() => refetch()}>
								重试
							</Button>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>用户名</TableHead>
									<TableHead>昵称</TableHead>
									<TableHead>邮箱</TableHead>
									<TableHead>手机号</TableHead>
									<TableHead>微信号</TableHead>
									<TableHead>性别</TableHead>
									<TableHead>状态</TableHead>
									<TableHead>是否超管</TableHead>
									<TableHead>创建时间</TableHead>
									<TableHead className="text-right">操作</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{!data?.list?.length ? (
									<TableRow>
										<TableCell colSpan={10} className="h-24 text-center">
											暂无数据
										</TableCell>
									</TableRow>
								) : (
									data.list.map((user) => {
										const statusInfo = statusConfig[user.status] || { label: '未知', variant: 'secondary' as const }
										return (
											<TableRow key={user.id}>
												<TableCell className="font-medium">{user.username}</TableCell>
												<TableCell>{user.nickname || '-'}</TableCell>
												<TableCell>{user.email}</TableCell>
												<TableCell>{user.phone || '-'}</TableCell>
												<TableCell>{user.wechat || '-'}</TableCell>
												<TableCell>{genderConfig[user.gender] || '未知'}</TableCell>
												<TableCell>
													<Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
												</TableCell>
												<TableCell>{user.is_super_admin ? '是' : '否'}</TableCell>
												<TableCell className="text-muted-foreground text-sm">
													{user.created_at}
												</TableCell>
												<TableCell className="text-right">
													<div className="flex items-center justify-end gap-1">
														<PermissionGuard permission="user:list">
															<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenDetail(user)} title="查看详情">
																<Eye className="h-4 w-4" />
															</Button>
														</PermissionGuard>
														<PermissionGuard permission="user:edit">
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																onClick={() => handleOpenEdit(user)}
																disabled={loadingEditUserId === user.id}
																title="编辑用户"
															>
																{loadingEditUserId === user.id ? (
																	<Loader2 className="h-4 w-4 animate-spin" />
																) : (
																	<Edit className="h-4 w-4" />
																)}
															</Button>
														</PermissionGuard>
														<PermissionGuard permission="user:edit">
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																onClick={() => { setStatusUser(user); setStatusDialogOpen(true) }}
																title={user.status === 1 ? '禁用用户' : '启用用户'}
															>
																{user.status === 1 ? (
																	<Ban className="h-4 w-4 text-destructive hover:text-destructive" />
																) : (
																	<CheckCircle className="h-4 w-4 text-green-600 hover:text-green-700" />
																)}
															</Button>
														</PermissionGuard>
														<PermissionGuard permission="user:transfer">
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																onClick={() => { setTransferTarget(user); setTransferDialogOpen(true) }}
																title="员工转交"
															>
																<ArrowRightLeft className="h-4 w-4" />
															</Button>
														</PermissionGuard>
														{currentUser?.is_super_admin && !user.is_super_admin && (
															<PermissionGuard permission="user:edit">
																<Button
																	variant="ghost"
																	size="icon"
																	className="h-8 w-8"
																	onClick={() => handleSetSuperAdmin(user)}
																	title="设为超管"
																>
																	<Shield className="h-4 w-4 text-blue-600" />
																</Button>
															</PermissionGuard>
														)}
														{currentUser?.is_super_admin && user.is_super_admin && String(currentUser?.id) !== String(user.id) && (
															<PermissionGuard permission="user:edit">
																<Button
																	variant="ghost"
																	size="icon"
																	className="h-8 w-8"
																	onClick={() => { setRevokeUser(user); setRevokeDialogOpen(true) }}
																	title="取消超管"
																>
																	<ShieldOff className="h-4 w-4 text-orange-600" />
																</Button>
															</PermissionGuard>
														)}
													</div>
												</TableCell>
											</TableRow>
										)
									})
								)}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{/* 分页 */}
			<ListPagination
				page={page}
				pageSize={pageSize}
				total={data?.total || 0}
				loading={isFetching}
				showPageNumbers={false}
				onPageChange={handlePageChange}
				onPageSizeChange={handlePageSizeChange}
			/>

			{/* 新增用户表单弹窗 */}
			<CreateUserForm
				open={createDialogOpen}
				onOpenChange={setCreateDialogOpen}
				onSuccess={() => refetch()}
			/>

			{/* 编辑用户弹窗 */}
			<EditUserForm
				open={editDialogOpen}
				onOpenChange={setEditDialogOpen}
				user={editingUser}
				onSuccess={() => refetch()}
			/>

			{/* 用户详情抽屉 */}
			<UserDetailDrawer
				open={detailDrawerOpen}
				onOpenChange={setDetailDrawerOpen}
				user={detailUser}
				isLoading={isDetailLoading}
			/>

			{/* 状态切换确认弹窗 */}
			<UserStatusConfirmDialog
				open={statusDialogOpen}
				onOpenChange={setStatusDialogOpen}
				user={statusUser}
			/>

			{/* 取消超管确认弹窗 */}
			<SuperAdminConfirmDialog
				open={revokeDialogOpen}
				onOpenChange={setRevokeDialogOpen}
				user={revokeUser}
				loading={superAdminMutation.isPending}
				onConfirm={handleConfirmRevoke}
			/>

			{/* 员工整批转交弹窗 */}
			<TransferUserDialog
				open={transferDialogOpen}
				onOpenChange={setTransferDialogOpen}
				user={transferTarget}
			/>
		</div>
	)
}
